<template>
  <div>
    <changelog ref="changelog"></changelog>

    <!-- mobile only: top bar with a burger + the user menu (on desktop this lives
         at the bottom of the sidebar instead, for a cleaner workspace) -->
    <v-app-bar
      app
      flat
      color="white"
      class="appbar-top"
      v-if="currentUser && $vuetify.breakpoint.smAndDown"
    >
      <v-app-bar-nav-icon @click="drawer = !drawer"></v-app-bar-nav-icon>
      <v-spacer></v-spacer>
      <notifications-bell variant="compact" />
      <user-menu variant="compact" @novedades="openChangelog" />
    </v-app-bar>

    <!-- sidebar -->
    <v-navigation-drawer
      class="appbar-drawer"
      :permanent="$vuetify.breakpoint.mdAndUp"
      :temporary="$vuetify.breakpoint.smAndDown"
      v-model="drawer"
      v-if="currentUser"
      app
    >
      <!--Logo-->
      <v-img
        height="54"
        contain
        :src="imageUrl(currentUser.selectedAgency.logoUrl)"
        alt="Logo"
        class="appbar-logo"
      >
      </v-img>
    <v-divider></v-divider>
    <!-- notification bell: the inquiry-centric feed (new consultas + client replies) -->
    <notifications-bell v-if="$vuetify.breakpoint.mdAndUp" variant="sidebar" />
    <!--Menus list. Items can be added in data property 'items'-->
    <v-list nav>
      <v-list-item
        v-for="item in items"
        :key="item.title"
        link
        exact
        :to="{ name: item.route }"
        active-class="appbar-item--active"
        v-if="item.allowedRoles.some(r => currentUser.displayRoles.includes(r))"
      >
        <v-list-item-icon>
          <span v-if="item.showsCount && item.count" class="nav-badge">
            {{ item.count }}
          </span>
          <hero-icon :icon="item.icon" :size="22" />
        </v-list-item-icon>

        <v-list-item-title class="nav-bar-content py-0" width="100vw">
          {{ item.title }}
        </v-list-item-title>
      </v-list-item>
    </v-list>

    <v-divider></v-divider>

    <!-- secondary / less-used sections, collapsed by default -->
    <v-list
      nav
      v-if="moreItems.some(i => i.allowedRoles.some(r => currentUser.displayRoles.includes(r)))"
    >
      <v-list-group no-action>
        <template v-slot:activator>
          <v-list-item-icon>
            <hero-icon :icon="MoreIcon" :size="22" />
          </v-list-item-icon>
          <v-list-item-title class="nav-bar-content">Más</v-list-item-title>
        </template>

        <v-list-item
          v-for="item in moreItems"
          :key="item.title"
          link
          exact
          :to="{ name: item.route }"
          active-class="appbar-item--active"
          v-if="item.allowedRoles.some(r => currentUser.displayRoles.includes(r))"
        >
          <v-list-item-icon>
            <span v-if="item.showsCount && item.count" class="nav-badge">
              {{ item.count }}
            </span>
            <hero-icon :icon="item.icon" :size="22" />
          </v-list-item-icon>
          <v-list-item-title class="nav-bar-content py-0">{{ item.title }}</v-list-item-title>
        </v-list-item>
      </v-list-group>
    </v-list>

      <!-- desktop: user menu pinned to the bottom of the sidebar -->
      <template v-slot:append>
        <user-menu
          v-if="$vuetify.breakpoint.mdAndUp"
          variant="full"
          @novedades="openChangelog"
        />
      </template>
    </v-navigation-drawer>
  </div>
</template>

<script>
import Changelog from "@/components/shared/Changelog";
import { mapGetters } from "vuex";
import HeroIcon from "@/components/shared/HeroIcon";
import NotificationsBell from "@/components/shared/NotificationsBell";
import { subscribeInquiries } from "@/services/inquiriesCable";
import { UnseenInquiriesCount } from "@/graphql/queries/inquiries";
import BoltIcon from "heroicons/24/outline/bolt.svg";
import HomeIcon from "heroicons/24/outline/home.svg";
import BookOpenIcon from "heroicons/24/outline/book-open.svg";
import ChatBubbleLeftRightIcon from "heroicons/24/outline/chat-bubble-left-right.svg";
import LinkIcon from "heroicons/24/outline/link.svg";
import UsersIcon from "heroicons/24/outline/users.svg";
import DocumentDuplicateIcon from "heroicons/24/outline/document-duplicate.svg";
import MoreIcon from "heroicons/24/outline/squares-2x2.svg";
import BanknotesIcon from "heroicons/24/outline/banknotes.svg";
import ChartBarIcon from "heroicons/24/outline/chart-bar.svg";
import SignalIcon from "heroicons/24/outline/signal.svg";
import CurrencyDollarIcon from "heroicons/24/outline/currency-dollar.svg";
import DocumentTextIcon from "heroicons/24/outline/document-text.svg";
import BuildingOfficeIcon from "heroicons/24/outline/building-office.svg";
import UserMenu from "@/components/shared/UserMenu";

export default {
  name: "AppBar",
  data() {
    return {
      drawer: false,
      MoreIcon,
      items: [
        {
          title: "Inicio",
          route: "Dashboard",
          allowedRoles: ["gendev", "admin", "properties"],
          icon: BoltIcon
        },
        {
          title: "Reportes",
          route: "Reports",
          allowedRoles: ["gendev", "admin", "properties"],
          icon: ChartBarIcon
        },
        {
          title: "En vivo",
          route: "Live",
          allowedRoles: ["gendev", "admin"],
          icon: SignalIcon
        },
        {
          title: "Consultas",
          route: "Consultas",
          allowedRoles: ["gendev", "admin", "properties"],
          icon: ChatBubbleLeftRightIcon,
          showsCount: true,
          count: 0
        },
        {
          title: "Inmuebles",
          route: "Inmuebles",
          allowedRoles: ["gendev", "admin", "properties"],
          icon: HomeIcon
        },
        {
          title: "Agenda",
          route: "Directory",
          allowedRoles: ["gendev", "admin", "properties"],
          icon: BookOpenIcon
        },
        {
          title: "Enlaces",
          route: "Enlaces",
          allowedRoles: ["gendev", "admin"],
          icon: LinkIcon
        },
        {
          title: "Staff",
          route: "staff",
          allowedRoles: ["admin"],
          icon: UsersIcon
        }
      ],
      // secondary / less-used sections, tucked into a collapsed "Más" group
      moreItems: [
        {
          title: "Incidencias",
          route: "MainTickets",
          allowedRoles: ["gendev", "admin"],
          icon: DocumentDuplicateIcon
        },
        {
          title: "Caja",
          route: "balances",
          allowedRoles: ["gendev", "admin"],
          icon: BanknotesIcon
        },
        {
          title: "Asientos",
          route: "payments",
          allowedRoles: ["gendev", "admin"],
          icon: CurrencyDollarIcon
        },
        {
          title: "Contratos",
          route: "Contracts",
          allowedRoles: ["gendev", "admin"],
          icon: DocumentTextIcon
        },
        {
          title: "Expensas",
          route: "buildings",
          allowedRoles: ["gendev", "admin"],
          icon: BuildingOfficeIcon
        }
      ],
      icons: {
        color: "white--text"
      }
    };
  },

  computed: {
    ...mapGetters(["currentUser"])
  },

  watch: {
    currentUser(user) {
      if (user) {
        let item = this.items.find(el => el.title == "Consultas");
        item.count = user.selectedAgency.unseenInquiries;
      }
    },

    // opening Consultas marks the list read server-side — sync the badge down
    $route(to) {
      if (to.name === "Consultas") setTimeout(() => this.refreshInquiriesCount(), 1500);
    }
  },

  created() {
    // live badge: new consultas / client replies bump it without a re-login
    this._sub = subscribeInquiries(e => {
      if (["inquiryCreated", "inquiryUpdated", "inquiryDeleted"].includes(e.type) || e.type === "comment") {
        this.refreshInquiriesCount();
      }
    });
  },

  beforeDestroy() {
    if (this._sub) this._sub.unsubscribe();
  },

  methods: {
    imageUrl(url) {
      return process.env.VUE_APP_RAILS_URL + url;
    },

    refreshInquiriesCount() {
      const agency = this.currentUser && this.currentUser.selectedAgency;
      if (!agency) return;
      this.$apollo
        .query({
          query: UnseenInquiriesCount,
          variables: { id: agency.id },
          fetchPolicy: "no-cache"
        })
        .then(res => {
          const item = this.items.find(el => el.title == "Consultas");
          if (item && res.data.agency) item.count = res.data.agency.unseenInquiries;
        })
        .catch(() => {});
    },

    openChangelog() {
      this.$refs.changelog.dialog = true;
    }
  },

  components: {
    Changelog,
    HeroIcon,
    UserMenu,
    NotificationsBell
  }
};
</script>

<style scoped>
/* mobile top app bar */
.appbar-top {
  border-bottom: 1px solid #ece7de;
}

/* white sidebar — Sartor branding */
.appbar-drawer {
  background: #fff;
  border-right: 1px solid #ece7de;
}
.appbar-logo {
  max-width: 156px;
  margin: 24px auto 20px;
}

/* nav items: warm ink by default */
.appbar-drawer .v-list-item__title {
  color: #1a1814;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: 0.01em;
}
.appbar-drawer .hero-icon {
  color: #6a645a;
}

/* comfortable list density */
.appbar-drawer .v-list-item {
  min-height: 48px;
}

/* active item: subtle warm-secondary highlight (red was too strong) */
.appbar-drawer .appbar-item--active {
  background: rgba(117, 100, 84, 0.14);
}
.appbar-drawer .appbar-item--active .v-list-item__title {
  color: #1a1814;
  font-weight: 600;
}
.appbar-drawer .appbar-item--active .hero-icon {
  color: #1a1814;
}

/* unseen-count badge: small, brand-toned pill, vertically centered at the row's
   right edge (hidden entirely when count is 0 via v-if) */
.nav-badge {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #b52118;
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  line-height: 1;
}
</style>
